import {
  SplitSupplyNoticeErrorReason,
  SplitSupplyPartnerNoticeChannel,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import type { PreviewPartnerSplitSupplyNoticeImportResponse } from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { NoticeImportDialog } from '~/pages/SplitSupplyPlan/components/NoticeImportDialog'
import { PartnerImportConfirmation } from '~/pages/SplitSupplyPlan/components/PartnerImportConfirmation'
import { type PartnerImportResult } from '~/pages/SplitSupplyPlan/components/PartnerImportResultBanner'
import {
  useImportPartnerSplitSupplyNotices,
  usePreviewPartnerNoticeImport,
  useUploadPartnerNoticeFile,
} from '~/pages/SplitSupplyPlan/hooks/usePartnerNoticeImport'
import { PARTNER_IMPORT_KEY_PREFIX } from '~/pages/SplitSupplyPlan/models/partnerImport'
import { type SectionTab } from '~/pages/SplitSupplyPlan/models/period'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 開いている断面 (XML はこの断面のものを選ぶ)。 */
  tab: SectionTab
  /**
   * XML の取込でスキップした供給地点があった時、 閉じる前に結果を渡す (呼び出し側がエラーバナーに出す)。
   * この時は成功のトーストは出さない。
   */
  onSkipped: (result: PartnerImportResult) => void
}

/** 内容が拒まれた (またはファイルの受け取りが切れた) ので、 選んだファイルでは続けられない reason。 */
const RESTART_REASONS: ReadonlySet<SplitSupplyNoticeErrorReason> = new Set([
  SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_UPLOAD_EXPIRED,
  SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_INVALID,
  SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_NOTHING,
  SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_TOO_LARGE,
])

/**
 * 他社の通告値の取込のダイアログ (流れは `NoticeImportDialog`)。 取り込む内容は新規 / 置き換え・地点・
 * 飛ばす地点・誤りで、 確認画面は `PartnerImportConfirmation`。
 */
export const ImportPartnerNoticesDialog = ({
  isOpen,
  onClose,
  tab,
  onSkipped,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const uploadFile = useUploadPartnerNoticeFile()
  const { mutateAsync: previewImport, isPending: isPreviewing } =
    usePreviewPartnerNoticeImport()
  const { mutateAsync: runImport, isPending: isImporting } =
    useImportPartnerSplitSupplyNotices()

  return (
    <NoticeImportDialog<PreviewPartnerSplitSupplyNoticeImportResponse>
      isOpen={isOpen}
      onClose={onClose}
      side={{
        prefix: PARTNER_IMPORT_KEY_PREFIX,
        uploadFile,
        preview: previewImport,
        runImport: async (input) => {
          const res = await runImport(input)
          if (
            res.channel === SplitSupplyPartnerNoticeChannel.XML &&
            res.skippedPointCount > 0
          ) {
            // XML でスキップした供給地点がある: 成功のトーストではなく、 件数と 「詳細を見る」 のエラーバナー
            onSkipped({
              registeredPointCount: res.registeredPointCount,
              skippedPointCount: res.skippedPointCount,
              skippedPoints: res.skippedPoints,
            })
            return undefined
          }
          // CSV は登録した数に加えて、 スキップした数も結果に出す (AC-S21。 エラーがあれば取り込めないので、 エラー件数は常に 0)
          return {
            title: t(`${PARTNER_IMPORT_KEY_PREFIX}.success`, {
              count: res.registeredPointCount,
            }),
            description:
              res.skippedPointCount > 0
                ? t(`${PARTNER_IMPORT_KEY_PREFIX}.result.skipped`, {
                    count: res.skippedPointCount,
                  })
                : undefined,
          }
        },
        isPreviewing,
        isImporting,
        restartReasons: RESTART_REASONS,
        changedReason:
          SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_CHANGED,
        renderConfirmation: ({ result, onRemoveBadFiles }) => (
          <PartnerImportConfirmation
            onRemoveBadFiles={onRemoveBadFiles}
            result={result}
          />
        ),
      }}
      tab={tab}
    />
  )
}
